import React, { useState, useEffect } from 'react';
import '../../pricing-strategies.css';
import '../../monitoring.css';

const Monitoring = () => {
  // Check if user is already authenticated in this session
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Hardcoded credentials
  const VALID_USERNAME = 'admin';
  const VALID_PASSWORD = 'admin';

  // Monitoring dashboard URL based on environment
  const getMonitoringUrl = () => {
    const apiUrl = process.env.REACT_APP_DRIVER_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5566/';
    // Remove trailing slash and append monitoring path
    const baseUrl = apiUrl.replace(/\/$/, '');
    return `${baseUrl}/shypvdriverapp/monitoring`;
  };

  const monitoringUrl = getMonitoringUrl();

  const shouldOpenExternally = (() => {
    try {
      const parsedUrl = new URL(monitoringUrl);
      return parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1';
    } catch (e) {
      return false;
    }
  })();

  const openMonitoringInNewTab = () => {
    window.open(monitoringUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    // Check if monitoring auth is stored in sessionStorage
    const monitoringAuth = sessionStorage.getItem('monitoring_auth');
    if (monitoringAuth === 'true') {
      setIsAuthenticated(true);
      setShowLoginModal(false);
    } else {
      setShowLoginModal(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');

    if (username === VALID_USERNAME && password === VALID_PASSWORD) {
      // Store auth state in sessionStorage
      sessionStorage.setItem('monitoring_auth', 'true');
      setIsAuthenticated(true);
      setShowLoginModal(false);
      setUsername('');
      setPassword('');
      setError('');
    } else {
      setError('Invalid username or password');
    }
  };

  const handleCloseModal = () => {
    // Navigate back if user closes modal without logging in
    window.history.back();
  };

  if (loading) {
    return (
      <div className="monitoring-loading-screen">
        <div className="monitoring-loader-card">
          <div className="monitoring-loader-spinner" />
          <h4>Preparing System Monitor</h4>
          <p>Loading secure dashboard workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="zones-list-container monitoring-page-shell">
      {/* Login Modal */}
      {showLoginModal && (
        <div className="monitoring-modal-overlay">
          <div className="monitoring-modal-content">
            <div className="monitoring-modal-header">
              <h3>
                <i className="fa fa-shield"></i>
                Monitoring Access
              </h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleLogin}>
              <div className="monitoring-modal-body">
                <div className="monitoring-security-chip">
                  <i className="fa fa-lock"></i>
                  Session-based access control enabled
                </div>
                <p>
                  Please enter your credentials to access the monitoring dashboard.
                </p>

                {error && (
                  <div className="monitoring-form-error" role="alert">
                    <i className="fa fa-exclamation-circle"></i>
                    {error}
                  </div>
                )}

                <div className="monitoring-form-group">
                  <label>
                    Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    className="monitoring-input"
                    autoFocus
                    required
                  />
                </div>

                <div className="monitoring-form-group">
                  <label>
                    Password
                  </label>
                  <div className="monitoring-password-row">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="monitoring-input"
                      required
                    />
                    <button
                      type="button"
                      className="monitoring-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>
              </div>
              <div className="monitoring-modal-footer">
                <button
                  type="button"
                  className="monitoring-btn monitoring-btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="monitoring-btn monitoring-btn-primary"
                >
                  <i className="fa fa-sign-in"></i>
                  Continue to Monitor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Monitoring Dashboard iframe */}
      {isAuthenticated && (
        <div className="monitoring-dashboard-shell">
          <div className="monitoring-dashboard-topbar">
            <div className="monitoring-dashboard-title">
              <i className="fa fa-television"></i>
              <span>System Monitoring Dashboard</span>
            </div>
            <button
              type="button"
              className="monitoring-btn monitoring-btn-secondary"
              onClick={openMonitoringInNewTab}
            >
              <i className="fa fa-external-link"></i>
              Open in new tab
            </button>
            <button
              type="button"
              className="monitoring-btn monitoring-btn-secondary"
              onClick={() => {
                sessionStorage.removeItem('monitoring_auth');
                setIsAuthenticated(false);
                setShowLoginModal(true);
              }}
            >
              <i className="fa fa-sign-out"></i>
              Sign out
            </button>
          </div>
          {shouldOpenExternally ? (
            <div className="monitoring-frame-blocked-card">
              <div className="monitoring-frame-blocked-icon">
                <i className="fa fa-ban"></i>
              </div>
              <h4>Embedded view is blocked</h4>
              <p>
                This monitoring server denies iframe embedding (X-Frame-Options: deny). Use external tab mode for local monitoring.
              </p>
              <button
                type="button"
                className="monitoring-btn monitoring-btn-primary"
                onClick={openMonitoringInNewTab}
              >
                <i className="fa fa-external-link"></i>
                Open Monitoring Dashboard
              </button>
            </div>
          ) : (
            <iframe
              src={monitoringUrl}
              title="System Monitoring Dashboard"
              className="monitoring-dashboard-frame"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Monitoring;
